import { lazy, Suspense, useContext, useEffect, useState } from "react";
import "./App.less";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// layouts
import AdminLayout from "./layouts/AdminLayout";
import AuthLayout from "./layouts/AuthLayout";

// views
import FullPageLoader from "./components/Loaders/FullPageLoader";
import ProfessionalProtectedRoutes from "./components/ProfessionalProtectedRoutes";
import ProtectedRoutes from "./components/ProtectedRoutes";
import BasicLayout from "./layouts/BasicLayout";
import { AdminContext } from "./providers/AdminContext";
import AuthServices from "./services/AuthServices";
import UserServices from "./services/UserServices";
import AbTest from "./views/admin/AbTest/AbTest";
import AdminHome from "./views/admin/AdminHome";
import Companies from "./views/admin/Companies/Companies";
import CompanyDetails from "./views/admin/Companies/CompanyDetails";
import Emails from "./views/admin/Emails/Emails";
import Buscar from "./views/admin/medicamentos/Buscar";
import NuevoConsumo from "./views/admin/medicamentos/NuevoConsumo";
import Notifications from "./views/admin/Notifications/Notifications";
import PatientDocuments from "./views/admin/PatientDocuments/PatientDocuments";
import LogBook from "./views/admin/Patients/LogBook";
import PatientMedicine from "./views/admin/Patients/PatientMedicine";
import PatientMedicineTaken from "./views/admin/Patients/PatientMedicineTaken";
import PatientResume from "./views/admin/Patients/PatientResume";
import SearchPatient from "./views/admin/Patients/SearchPatient";
import SelfEvaluations from "./views/admin/Patients/SelfEvaluations";
import SelfMeasure from "./views/admin/Patients/SelfMeasure";
import Pills from "./views/admin/Pills/Pills";
import PillsSent from "./views/admin/Pills/PillsSent";
import ProfessionalNotes from "./views/admin/ProfessionalNotes/ProfessionalNotes";
import ChangePassword from "./views/admin/Profile/ChangePassword";
import PersonalDetails from "./views/admin/Profile/PersonalDetails";
import Profile from "./views/admin/Profile/Profile";
import ProtocolDetails from "./views/admin/Protocols/ProtocolDetails";
import Protocols from "./views/admin/Protocols/Protocols";
import RequestsPendings from "./views/admin/Requests/RequestsPendings";
import AddSubscription from "./views/admin/Subscriptions/AddSubscription";
import EditSubscription from "./views/admin/Subscriptions/EditSubscription";
import PendingPatientInvitations from "./views/admin/Subscriptions/PendingPatientInvitations";
import SubscriptionModules from "./views/admin/Subscriptions/SubscriptionModules";
import Subscriptions from "./views/admin/Subscriptions/Subscriptions";
import Contacts from "./views/admin/Users/Contacts";
import PendingInvitations from "./views/admin/Users/PendingInvitations";
import UsersInactives from "./views/admin/Users/UsersInactives";
import ForgotPassword from "./views/auth/ForgotPassword";
import ForgotPasswordVerificationCode from "./views/auth/ForgotPasswordVerificationCode";
import Login from "./views/auth/Login";
import Register from "./views/auth/Register";
import VerificationCode from "./views/auth/VerificationCode";
import Error404 from "./views/pages/Error404";
const CreatePaymentMP = lazy(
  () => import("./views/pages/MercadoPago/CreatePaymentMP")
);
const StatusPaymentMP = lazy(
  () => import("./views/pages/MercadoPago/StatusPaymentMP")
);
const CreateCreditCardMP = lazy(
  () => import("./views/pages/MercadoPago/CreateCreditCardMP")
);

const App = () => {
  const [loading, setLoading] = useState(true);
  const { setProfile, removeProfile } = useContext(AdminContext);
  const [isSuper, setIsSuper] = useState(false);
  const [isCoord, setIsCoord] = useState(false);
  const [isSuperOrAdmin, setIsSuperOrAdmin] = useState(false);

  useEffect(() => {
    handleTokenChange();
  }, []);

  const handleTokenChange = async () => {
    setLoading(true);
    if (AuthServices.getToken()) {
      try {
        const user = await UserServices.getProfile();
        setProfile({
          ...user.data.my_contact_info,
          can_invite: user.data.can_invite,
        });
        setLoading(false);
        setIsSuper(user.data.my_contact_info.user.rol === "eira-super-admin");
        setIsCoord(
          user.data.my_contact_info.user.rol === "eira-company-coordinator"
        );
        setIsSuperOrAdmin(
          user.data.my_contact_info.user.rol === "eira-company-admin" ||
            user.data.my_contact_info.user.rol === "eira-super-admin"
        );
      } catch (e) {
        console.error("handleTokenChange error: ", e);
        AuthServices.removeToken();
        removeProfile();
        setLoading(false);
      }
    } else {
      AuthServices.removeToken();
      removeProfile();
      setLoading(false);
      setIsSuper(false);
      setIsSuperOrAdmin(false);
    }
  };

  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="auth" element={<AuthLayout />}>
          <Route
            path="login"
            element={<Login onSuccess={handleTokenChange} />}
          />
          <Route path="register/:username/:role/:code" element={<Register />} />
          <Route path="register/:username/:code" element={<Register />} />
          <Route path="register" element={<Register />} />
          <Route
            path="verification-code/:username/:code"
            element={<VerificationCode />}
          />
          <Route
            path="forgot-password/verify/:username/:code"
            element={<ForgotPasswordVerificationCode />}
          />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="" element={<Navigate replace to={"/auth/login"} />} />
        </Route>

        <Route path="admin" element={<AdminLayout />}>
          <Route element={<ProtectedRoutes />}>
            {/* START - Rutas no visibles si el profesiona no tiene cargado su perfil  */}
            <Route element={<ProfessionalProtectedRoutes />}>
              <Route path="" element={<AdminHome />} />

              {(isSuperOrAdmin || isCoord) && (
                <>
                  <Route path="subscriptions">
                    <Route path=":id" element={<EditSubscription />} />
                    <Route
                      path="add/:company_id"
                      element={<AddSubscription />}
                    />
                    <Route
                      path="modules/:id"
                      element={<SubscriptionModules />}
                    />
                    <Route path="" element={<Subscriptions />} />
                  </Route>

                  <Route path="companies">
                    <Route path="" element={<Companies />} />
                    <Route path=":id" element={<CompanyDetails />} />
                  </Route>

                  <Route path="requests">
                    <Route path="pendings/:id" element={<RequestsPendings />} />
                  </Route>
                  <Route
                    path="pending-patients-invitations"
                    element={<PendingPatientInvitations />}
                  />
                </>
              )}

              {isSuper && (
                <>
                  <Route path="protocols">
                    <Route path="" element={<Protocols />} />
                    <Route path=":id" element={<ProtocolDetails />} />
                  </Route>
                  <Route path="pills">
                    <Route path="" element={<Pills />} />
                    <Route path=":id" element={<ProtocolDetails />} />
                  </Route>
                  <Route path="abtest">
                    <Route path="" element={<AbTest />} />
                  </Route>
                </>
              )}

              <Route path="users">
                <Route
                  path="home_inactives/:period"
                  element={<UsersInactives />}
                />
                <Route
                  path="home_inactives/:period/:subscription_id"
                  element={<UsersInactives />}
                />
                <Route path="inactives/:id" element={<UsersInactives />} />
              </Route>

              <Route path="buscar-paciente">
                <Route path="" element={<SearchPatient />} />
                <Route
                  path=":username/professional-notes"
                  element={<ProfessionalNotes />}
                />
                <Route
                  path=":username/notifications/:days"
                  element={<Notifications />}
                />
                <Route path=":username/emails/:days" element={<Emails />} />
                <Route path=":username/pills/:days" element={<PillsSent />} />
                <Route
                  path=":username/personal-details"
                  element={<PersonalDetails />}
                />
                <Route
                  path=":username/medicinas-paciente/tomas/:subscription_medicine_patient_id"
                  element={<PatientMedicineTaken />}
                />
                <Route
                  path=":username/medicinas-paciente"
                  element={<PatientMedicine />}
                />
                <Route
                  path=":username/autoevaluaciones"
                  element={<SelfEvaluations />}
                />
                <Route
                  path=":username/automediciones"
                  element={<SelfMeasure />}
                />
                <Route path=":username/diario-registro" element={<LogBook />} />
                <Route
                  path=":username/patient-documents"
                  element={<PatientDocuments />}
                />
                <Route path=":username" element={<PatientResume />} />
              </Route>

              <Route path="medicamentos">
                <Route path="buscar/:fromUrl/:userName" element={<Buscar />} />
                <Route
                  path="buscar/:fromUrl/:subscriptionId/:userName"
                  element={<Buscar />}
                />
                <Route path="nuevo-consumo">
                  <Route
                    path=":medicineId/:userName"
                    element={<NuevoConsumo />}
                  />
                  <Route path=":userName" element={<NuevoConsumo />} />
                  <Route path="" element={<NuevoConsumo />} />
                </Route>
              </Route>

              <Route path="contacts">
                <Route path="" element={<Contacts />} />
                <Route path="pendings" element={<PendingInvitations />} />
                <Route path=":id" element={<PatientResume />} />
              </Route>
            </Route>
            {/* END - Rutas no visibles si el profesiona no tiene cargado su perfil  */}
            <Route path="change-password" element={<ChangePassword />} />

            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="error404" element={<Error404 />} />
          <Route
            path="*"
            element={<Navigate replace to={"/admin/error404"} />}
          />
          <Route path="" element={<Navigate replace to={"/auth/login"} />} />
        </Route>

        <Route path="">
          <Route path="" element={<BasicLayout />}>
            <Route
              path="create-payment-mp"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <CreatePaymentMP />
                </Suspense>
              }
            />
            <Route
              path="status-payment-mp/:paymentId"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <StatusPaymentMP />
                </Suspense>
              }
            />
            <Route
              path="create-credit-card-mp"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <CreateCreditCardMP />
                </Suspense>
              }
            />
            <Route path="" element={<Navigate replace to={"/auth/login"} />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
